import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiDelete, apiGet, apiPatch, apiPost, operationsClient } from '@/lib/api';
import { contactKeys, locationKeys } from '@/lib/queryKeys';
import type { ContactAddress } from '@/types/contact';
import { normalizeContactAddress } from './useContacts';

export interface UpsertContactAddressDto {
  title?: string | null;
  label?: string | null;
  isBilling?: boolean;
  isService?: boolean;
  street1: string;
  street2?: string | null;
  city: string;
  state: string;
  postalCode: string;
  country?: string | null;
  isPrimary?: boolean;
  isTaxExempt?: boolean;
  taxExemptCertificate?: string | null;
  taxExemptExpiry?: string | null;
  accessNotes?: string | null;
  customFields?: Record<string, unknown>[];
}

export function useContactAddresses(contactId: string) {
  return useApiQuery<ContactAddress[]>({
    queryKey: contactKeys.addresses(contactId),
    queryFn: async () => {
      const raw = await apiGet<Record<string, unknown>[]>(
        operationsClient,
        `/contacts/${contactId}/addresses`,
      );
      return raw.map(normalizeContactAddress);
    },
    enabled: Boolean(contactId),
    showErrorToast: false,
  });
}

function invalidateContactAddressQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  contactId: string,
) {
  void queryClient.invalidateQueries({ queryKey: contactKeys.addresses(contactId) });
  void queryClient.invalidateQueries({ queryKey: contactKeys.detail(contactId) });
  void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
  // The contact page's Locations pane reads this list, not the address list.
  void queryClient.invalidateQueries({ queryKey: contactKeys.locations(contactId) });
  // A contact address is a service location: the job card and the calendar
  // sheet read its access notes from the location detail, so an edited gate
  // code must not stay cached there.
  void queryClient.invalidateQueries({ queryKey: locationKeys.all() });
}

export function useCreateContactAddress(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<ContactAddress, UpsertContactAddressDto>({
    mutationFn: async (dto) => {
      const raw = await apiPost<Record<string, unknown>>(
        operationsClient,
        `/contacts/${contactId}/addresses`,
        dto,
      );
      return normalizeContactAddress(raw);
    },
    onSuccess: () => {
      invalidateContactAddressQueries(queryClient, contactId);
      toast.success('Address created');
    },
  });
}

export interface UpdateContactAddressOptions {
  /**
   * NOT-586: suppress this hook's own toasts when the update is one step of a
   * larger save the caller is already reporting on.
   *
   * The lead sheet rewrites a lead's linked ContactAddress before PATCHing the
   * lead. Left to its defaults this hook announces `Address updated` on the way
   * through, so one Save click produced two success toasts — and a lead PATCH
   * that failed afterwards followed a success toast with an error. On the
   * failure path the caller's own message collided with this hook's automatic
   * error toast the same way.
   *
   * Only the toasts are silenced. Invalidation still runs: the row really did
   * change, and the contact caches have to hear about it either way.
   */
  silent?: boolean;
}

export function useUpdateContactAddress(
  contactId: string,
  options: UpdateContactAddressOptions = {},
) {
  const queryClient = useQueryClient();
  const { silent = false } = options;

  return useApiMutation<
    ContactAddress,
    { addressId: string; dto: Partial<UpsertContactAddressDto> }
  >({
    mutationFn: async ({ addressId, dto }) => {
      const raw = await apiPatch<Record<string, unknown>>(
        operationsClient,
        `/contacts/${contactId}/addresses/${addressId}`,
        dto,
      );
      return normalizeContactAddress(raw);
    },
    onSuccess: () => {
      invalidateContactAddressQueries(queryClient, contactId);
      if (!silent) toast.success('Address updated');
    },
    showErrorToast: !silent,
  });
}

export function useDeleteContactAddress(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<{ deleted: boolean }, string>({
    mutationFn: async (addressId) =>
      apiDelete<{ deleted: boolean }>(
        operationsClient,
        `/contacts/${contactId}/addresses/${addressId}`,
      ),
    onSuccess: () => {
      invalidateContactAddressQueries(queryClient, contactId);
      toast.success('Address deleted');
    },
  });
}

export function useSetPrimaryContactAddress(contactId: string) {
  const queryClient = useQueryClient();

  return useApiMutation<ContactAddress, string>({
    mutationFn: async (addressId) => {
      const raw = await apiPost<Record<string, unknown>>(
        operationsClient,
        `/contacts/${contactId}/addresses/${addressId}/set-primary`,
        {},
      );
      return normalizeContactAddress(raw);
    },
    onSuccess: () => {
      invalidateContactAddressQueries(queryClient, contactId);
      toast.success('Primary address updated');
    },
  });
}
